import { useState, useEffect, useLayoutEffect, useRef, memo, createContext, useContext, useMemo, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';

import useResponsive from 'src/hooks/useResponsive';
import useAuth from 'src/hooks/useAuth';
import { getChatsAtendimento, getChatsFinalizados, getChatsGeral, getChatsPendente, getCountsChats, setDrawerPesquisas, getChatsGrupos, getPesquisarChats, getMaisChatsAtendimento } from 'src/redux/slices/chat';
import { useSelector, useDispatch } from 'src/redux/store';
import Scrollbar from 'src/components/Scrollbar';
import Iconify from 'src/components/Iconify';
import SkeletonListaChats from 'src/components/skeleton/chats/SkeletonListaChats';
import v2_actionFinalizarMultiplosChats from 'src/actions/v2/chats/v2_actionFinalizarMultiplosChats';
import { badgeFuncNumber, verifyRules, verifyFeatures } from 'src/utils/functions';
import { BOTAO_RELEVO_SECUNDARIO, BOTAO_RELEVO_SECUNDARIO_ATIVO, INPUT_AFUNDADO } from 'src/utils/buttonStyles';
import { ButtonLoading } from 'src/@/components/use/ButtonLoading';
import ModalConfimacao from 'src/components/modal/ModalConfimacao';

import { Tabs, TabsContent, TabsList, TabsTrigger } from "src/@/components/ui/tabs";
import { Button } from 'src/@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from 'src/@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'src/@/components/ui/tooltip';
import { FormControl, InputBase, MenuItem, Select, styled } from '@mui/material';
import { Checkbox } from 'src/@/components/ui/checkbox';

import ChatConversationList from './ChatsList/ChatConversationList';
import ChatConversationListFinalizado from './ChatsList/ChatConversationListFinalizado';
import ChatConversationListGeral from './ChatsList/ChatConversationListGeral';
import ChatConversationListPendente from './ChatsList/ChatConversationListPendente';
import ChatConversationListArquivado from './ChatsList/ChatConversationListArquivado';
import { getDivisoes } from 'src/redux/slices/chat-arquivo';
import ChatListPesquisas from './ChatsList/ChatListPesquisas';
import { ChatContactSearch } from 'src/components/_chats/chat';
import MenuOptionsNovo from 'src/sections/@dashboard/negocios/MenuOptionsNovo';
import CommentsPostList from 'src/sections/@chats/comments/CommentsPostList';
import { setActiveChatTab } from 'src/redux/slices/socialComments';
// ----------------------------------------------------------------------

const TAB_COLORS = {
  '1': { bg: 'bg-violet-500', text: 'text-violet-600', light: 'bg-violet-50 dark:bg-violet-500/10' },
  '2': { bg: 'bg-red-500', text: 'text-red-600', light: 'bg-red-50 dark:bg-red-500/10' },
  '3': { bg: 'bg-blue-500', text: 'text-blue-600', light: 'bg-blue-50 dark:bg-blue-500/10' },
  '4': { bg: 'bg-emerald-500', text: 'text-emerald-600', light: 'bg-emerald-50 dark:bg-emerald-500/10' },
};

const CompactInput = styled(InputBase)(({ theme }) => ({
  'label + &': { marginTop: theme.spacing(1) },
  '& .MuiInputBase-input': {
    borderRadius: 4,
    position: 'relative',
    backgroundColor: '#ffffff',
    border: '1px solid #d1d5db',
    fontSize: 12,
    padding: '4px 8px',
    color: '#374151',
    transition: theme.transitions.create(['border-color', 'box-shadow']),
    '&:hover': { borderColor: '#9ca3af' },
    '&:focus': { borderColor: '#3b82f6', boxShadow: '0 0 0 1px #3b82f6' },
  },
}));

const FilterSelect = ({ label, value, onChange, options, chipColor, checkColor }) => {
  const { t } = useTranslation('chat');
  return (
  <div className="space-y-1">
    <label className="text-[11px] font-medium text-gray-500 dark:text-neutral-400 uppercase tracking-wider">{label}</label>
    <FormControl fullWidth>
      <Select
        multiple
        size="small"
        value={value}
        onChange={onChange}
        displayEmpty
        renderValue={(selected) =>
          selected.length === 0 ? (
            <span className="text-[11px] text-gray-400">{t('sidebar.selectDots')}</span>
          ) : (
            <div className="flex flex-wrap gap-1">
              {selected.map((v) => (
                <span key={v} className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${chipColor}`}>
                  {v}
                </span>
              ))}
            </div>
          )
        }
        input={<CompactInput />}
        MenuProps={{
          sx: { zIndex: 99999 },
          PaperProps: { sx: { maxHeight: 200 } }
        }}
        sx={{
          '& .MuiSelect-select': { padding: '4px 8px', fontSize: '12px', minHeight: '26px', display: 'flex', alignItems: 'center' },
          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#9ca3af' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#3b82f6', borderWidth: '1px' },
        }}
      >
        {options.map((name) => (
          <MenuItem key={name} value={name} sx={{ py: 0.5, px: 1 }}>
            <div className="flex items-center space-x-1.5">
              <Checkbox
                checked={value.indexOf(name) > -1}
                size="small"
                sx={{ color: '#6b7280', padding: '2px', '&.Mui-checked': { color: checkColor } }}
              />
              <span className="text-xs text-gray-900 dark:text-white">{name}</span>
            </div>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  </div>
  );
};

const SidebarFilterButton = memo(function SidebarFilterButton({
  filters,
  handlers,
  listaTagsReduce,
  listaCanaisReduce,
  listaFunilReduce,
  showFunis = false,
  showGrupos = false,
  canMassFinalize = false,
  onMassFinalize,
  loadingMassFinalize = false,
}) {
  const { t } = useTranslation('chat');
  const { tags, funis, canais, naoLidos, grupos = [], isFiltered } = filters;
  const activeCount = [tags, funis, canais, naoLidos, grupos].filter(a => a.length > 0).length;
  const [openModalConfimacao, setOpenModalConfimacao] = useState(false);

  return (
    <>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className={`h-8 w-8 flex-shrink-0 rounded-lg ${
              isFiltered ? BOTAO_RELEVO_SECUNDARIO_ATIVO : BOTAO_RELEVO_SECUNDARIO
            }`}
          >
            <div className="relative">
              <Iconify icon="solar:filter-bold" width={15} />
              {activeCount > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[13px] h-[13px] flex items-center justify-center rounded-full bg-violet-500 text-white text-[8px] font-bold px-0.5">
                  {activeCount}
                </span>
              )}
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-72 p-3 space-y-2.5"
          align="end"
          side="bottom"
          sideOffset={4}
        >
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-semibold text-gray-900 dark:text-white">{t('sidebar.filters')}</span>
            {isFiltered && (
              <button
                onClick={handlers.onClearFilters}
                className="text-[10px] font-medium text-red-500 hover:text-red-600 transition-colors"
              >
                {t('sidebar.clearAll')}
              </button>
            )}
          </div>

          {listaTagsReduce.length > 0 && (
            <FilterSelect
              label={t('sidebar.tags')}
              value={tags}
              onChange={handlers.onChangeTags}
              options={listaTagsReduce}
              chipColor="bg-blue-100 text-blue-800 dark:bg-blue-500/20 dark:text-blue-300"
              checkColor="#3b82f6"
            />
          )}

          <FilterSelect
            label={t('sidebar.status')}
            value={naoLidos}
            onChange={handlers.onChangeNaoLidos}
            options={[t('sidebar.unreadChats'), t('sidebar.readChats')]}
            chipColor="bg-emerald-100 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-300"
            checkColor="#10b981"
          />

          {listaCanaisReduce.length > 0 && (
            <FilterSelect
              label={t('sidebar.channel')}
              value={canais}
              onChange={handlers.onChangeCanais}
              options={listaCanaisReduce}
              chipColor="bg-purple-100 text-purple-800 dark:bg-purple-500/20 dark:text-purple-300"
              checkColor="#8b5cf6"
            />
          )}

          {showFunis && listaFunilReduce.length > 0 && (
            <FilterSelect
              label={t('sidebar.funnel')}
              value={funis}
              onChange={handlers.onChangeFunis}
              options={listaFunilReduce}
              chipColor="bg-orange-100 text-orange-800 dark:bg-orange-500/20 dark:text-orange-300"
              checkColor="#f97316"
            />
          )}

          {showGrupos && (
            <FilterSelect
              label={t('sidebar.groupType')}
              value={grupos}
              onChange={handlers.onChangeGrupos}
              options={[t('sidebar.groupsLabel'), t('sidebar.individualsLabel')]}
              chipColor="bg-indigo-100 text-indigo-800 dark:bg-indigo-500/20 dark:text-indigo-300"
              checkColor="#6366f1"
            />
          )}

          {canMassFinalize && (
            <div className="pt-2 mt-1 border-t border-gray-200 dark:border-neutral-700">
              <ButtonLoading
                variant="destructive"
                size="sm"
                onClick={() => setOpenModalConfimacao(true)}
                loading={loadingMassFinalize}
                className="w-full h-7 text-xs"
              >
                <Iconify icon="icon-park-solid:clear-format" className="mr-1" width={12} />
                {t('sidebar.massFinalize')}
              </ButtonLoading>
            </div>
          )}
        </PopoverContent>
      </Popover>
      {canMassFinalize && (
        <ModalConfimacao
          open={openModalConfimacao}
          onClose={() => setOpenModalConfimacao(false)}
          onConfirm={async () => {
            await onMassFinalize?.();
            setOpenModalConfimacao(false);
          }}
          loadingFinalizar={loadingMassFinalize}
          mensagem={t('sidebar.massFinalizeConfirm')}
        />
      )}
    </>
  );
});

const ChatSidebarContext = createContext({
  isOpen: true,
  layoutMode: 'navbar',
  filters: {
    tags: [],
    funis: [],
    canais: [],
    naoLidos: []
  },
  handlers: {}
});

export const useChatSidebar = () => useContext(ChatSidebarContext);

const ScrollableComponent = ({ children }) => {
  const scrollRef = useRef(null);
  const scrollPosition = useRef(0);
  const { layoutMode } = useChatSidebar();

  useLayoutEffect(() => {
    if (scrollRef.current) {
      scrollPosition.current = scrollRef.current.scrollTop;
    }
  }, [children]);

  useLayoutEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollPosition.current;
    }
  }, [children]);

  return (
    <Scrollbar sx={{ height: layoutMode === 'sidebar' ? 'calc(100vh - 140px)' : 'calc(100vh - 200px)' }} ref={scrollRef}>
      {children}
    </Scrollbar>
  );
};

const SearchResultsScrollbar = ({ children }) => {
  const { layoutMode } = useChatSidebar();
  return (
    <Scrollbar sx={{ height: layoutMode === 'sidebar' ? '100vh' : 'calc(90vh - 200px)' }}>
      {children}
    </Scrollbar>
  );
};

const SyncWarning = memo(function SyncWarning({ status, channelName }) {
  const { t } = useTranslation('chat');
  if (status !== 'sync') return null;

  return (
    <div className="flex items-center gap-3 mx-3 mb-2 px-3.5 py-2.5 rounded-xl bg-amber-50 dark:bg-amber-500/10 border border-amber-200/60 dark:border-amber-500/20">
      <div className="relative flex-shrink-0">
        <div className="w-8 h-8 rounded-lg bg-amber-100 dark:bg-amber-500/20 flex items-center justify-center">
          <Iconify
            icon="svg-spinners:pulse-rings-multiple"
            className="text-amber-600 dark:text-amber-400"
            width={18}
          />
        </div>
      </div>
      <div className="flex flex-col min-w-0">
        <span className="text-xs font-semibold text-amber-800 dark:text-amber-300 leading-tight truncate">
          {t('sidebar.syncing', { channel: channelName })}
        </span>
        <span className="text-[10px] text-amber-600 dark:text-amber-400/80 mt-0.5 leading-tight">
          {t('sidebar.syncingHint')}
        </span>
      </div>
    </div>
  );
});


// Memoized tab content components
const ItemAtendendo = memo(function ItemAtendendo({
  chats,
  cliente,
  loading,
  listaFunilReduce,
  listaTagsReduce,
  listaCanaisReduce,
  dataFiltered,
  expanded,
  onChangeAccordion,
  onMoreChats
}) {
  const { isOpen, filters, handlers } = useChatSidebar();

  if (loading) return <SkeletonListaChats />;

  return (
    <ScrollableComponent>
      <ChatConversationList
        cliente={cliente}
        chats={chats}
        isOpenSidebar={isOpen}
        expanded={expanded}
        listaFunilReduce={listaFunilReduce}
        listaTagsReduce={listaTagsReduce}
        listaCanaisReduce={listaCanaisReduce}
        tagsSelecionadas={filters.tags}
        funisSelecionadas={filters.funis}
        canaisSelecionados={filters.canais}
        chatsNaoLidos={filters.naoLidos}
        dataFiltered={dataFiltered}
        changeChatsNaoLidos={handlers.onChangeNaoLidos}
        changeAccordion={onChangeAccordion}
        changeFunis={handlers.onChangeFunis}
        changeTags={handlers.onChangeTags}
        changeCanais={handlers.onChangeCanais}
        limparFiltro={handlers.onClearFilters}
        isFiltered={filters.isFiltered}
        onMoreChats={onMoreChats}
      />
    </ScrollableComponent>
  );
});

const ItemPendente = memo(function ItemPendente({
  chats,
  loading,
  listaTagsReduce,
  listaCanaisReduce
}) {
  const { isOpen, filters, handlers } = useChatSidebar();

  if (loading) return <SkeletonListaChats />;

  return (
    <ScrollableComponent>
      <ChatConversationListPendente
        chats={chats}
        isOpenSidebar={isOpen}
        listaTagsReduce={listaTagsReduce}
        listaCanaisReduce={listaCanaisReduce}
        tagsSelecionadas={filters.tags}
        canaisSelecionados={filters.canais}
        changeTags={handlers.onChangeTags}
        changeCanais={handlers.onChangeCanais}
        chatsNaoLidos={filters.naoLidos}
        changeChatsNaoLidos={handlers.onChangeNaoLidos}
        limparFiltro={handlers.onClearFilters}
        isFiltered={filters.isFiltered}
      />
    </ScrollableComponent>
  );
});

const ItemFinalizado = memo(function ItemFinalizado({
  chats,
  cliente,
  loading,
  listaTagsReduce,
  listaCanaisReduce
}) {
  const { isOpen, filters, handlers } = useChatSidebar();

  if (loading) return <SkeletonListaChats />;

  return (
    <ScrollableComponent>
      <ChatConversationListFinalizado
        cliente={cliente}
        chats={chats}
        isOpenSidebar={isOpen}
        listaTagsReduce={listaTagsReduce}
        listaCanaisReduce={listaCanaisReduce}
        tagsSelecionadas={filters.tags}
        canaisSelecionados={filters.canais}
        chatsNaoLidos={filters.naoLidos}
        changeTags={handlers.onChangeTags}
        changeCanais={handlers.onChangeCanais}
        changeChatsNaoLidos={handlers.onChangeNaoLidos}
        limparFiltro={handlers.onClearFilters}
        isFiltered={filters.isFiltered}
      />
    </ScrollableComponent>
  );
});

const ItemArquivado = memo(function ItemArquivado({
  listaTagsReduce,
  listaCanaisReduce
}) {
  const { isOpen, filters } = useChatSidebar();

  return (
    <ScrollableComponent>
      <ChatConversationListArquivado
        isOpenSidebar={isOpen}
        listaTagsReduce={listaTagsReduce}
        listaCanaisReduce={listaCanaisReduce}
        tagsSelecionadas={filters.tags}
        canaisSelecionados={filters.canais}
        chatsNaoLidos={filters.naoLidos}
      />
    </ScrollableComponent>
  );
});

const ItemGeral = memo(function ItemGeral({
  chats,
  chatInfo,
  cliente,
  loading,
  listaTagsReduce,
  listaCanaisReduce
}) {
  const { isOpen, filters, handlers } = useChatSidebar();

  // Skeleton so quando ainda nao ha dados: ao voltar para a aba com a lista ja
  // carregada, o refresh roda por baixo sem apagar o que esta na tela.
  if (loading && !(chats?.length > 0)) return <SkeletonListaChats />;

  return (
    <ScrollableComponent>
      <ChatConversationListGeral
        cliente={cliente}
        chats={chats}
        chat={chatInfo}
        isOpenSidebar={isOpen}
        listaTagsReduce={listaTagsReduce}
        listaCanaisReduce={listaCanaisReduce}
        tagsSelecionadas={filters.tags}
        canaisSelecionados={filters.canais}
        chatsNaoLidos={filters.naoLidos}
        changeTags={handlers.onChangeTags}
        changeCanais={handlers.onChangeCanais}
        changeChatsNaoLidos={handlers.onChangeNaoLidos}
        limparFiltro={handlers.onClearFilters}
        isFiltered={filters.isFiltered}
      />
    </ScrollableComponent>
  );
});

export default function ChatSidebar() {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { pathname } = useLocation();
  const isDesktop = useResponsive('up', 'md');
  const { user, cliente } = useAuth();
  const { t } = useTranslation('chat');

  // State
  const [openSidebar, setOpenSidebar] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchFocused, setSearchFocused] = useState(false);
  const [valueScrollable, setValueScrollable] = useState('1');
  const [tagsSelecionadas, setTagsSelecionadas] = useState([]);
  const [funisSelecionadas, setFunisSelecionadas] = useState([]);
  const [canaisSelecionados, setCanaisSelecionados] = useState([]);
  const [chatsNaoLidos, setChatsNaoLidos] = useState([]);
  const [gruposSelecionados, setGruposSelecionados] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [loadingLimparChats, setLoadingLimparChats] = useState(false);
  // Arquivadas não são uma aba: elas substituem o conteúdo da sidebar, como já
  // acontece com os resultados de busca (renderSearchResults).
  const [mostrarArquivadas, setMostrarArquivadas] = useState(false);
  const layoutMode = localStorage.getItem('layoutMode') || 'sidebar';

  // Redux selectors
  const {
    chatsAtendimento,
    chatInfo,
    chatsPendente,
    chatsGeral,
    chatsFinalizado,
    pesquisasChat,
    drawerPesquisasChat,
    countsListaChats,
    loadingListaChatsAtendendo,
    loadingListaChatsPendente,
    loadingListaChatsGeral,
    loadingListaChatsFinalizados,
    listaFunil,
    listaTags,
    loadingPesquisarChat,
    chatsGrupos
  } = useSelector((state) => state.chat);
  const { stateSyncWpp } = useSelector((state) => state.global);
  const totalArquivadas = useSelector((state) => state.chatArquivo.totalArquivadas);
  const activeChatTab = useSelector((state) => state.socialComments.activeChatTab);
  const unreadComments = useSelector((state) => state.socialComments.unreadComments);

  // Memoized data transformations
  const listaFunilReduce = useMemo(() =>
    listaFunil?.map((item) => item.nome_funil) || [], [listaFunil]);

  const listaTagsReduce = useMemo(() =>
    listaTags?.map((item) => item.nome) || [], [listaTags]);

  // As paginas extras do "Carregar Mais" agora vivem dentro de chatsAtendimento
  // (getMaisChatsAtendimento), entao aqui so resta juntar os grupos.
  // O dedupe por _id nao e teorico: buscarChatsAtendendo no backend nao filtra
  // isGroup, ao contrario das queries irmas, entao basta um chat de grupo ganhar
  // statusRoom "Atendendo" + direciona_usuarios para ele cair nos dois arrays e
  // renderizar duas vezes com a mesma key.
  const tableData = useMemo(() => {
    const vistos = new Set();
    return chatsAtendimento.concat(chatsGrupos || []).filter((chat) => {
      const id = String(chat?._id);
      if (vistos.has(id)) return false;
      vistos.add(id);
      return true;
    });
  }, [chatsAtendimento, chatsGrupos]);

  // Channel lists by tab
  const listaCanaisReduceAtendimento = useMemo(() =>
    [...new Set(tableData.map(chat => chat.nome_canal).filter(Boolean))], [tableData]);

  const listaCanaisReduceGeral = useMemo(() =>
    [...new Set(chatsGeral?.map(chat => chat.nome_canal).filter(Boolean) || [])], [chatsGeral]);

  const listaCanaisReduceFinalizado = useMemo(() =>
    [...new Set(chatsFinalizado?.map(chat => chat.nome_canal).filter(Boolean) || [])], [chatsFinalizado]);

  const listaCanaisReduce = useMemo(() => {
    if (valueScrollable === '3') return listaCanaisReduceGeral;
    if (valueScrollable === '4') return listaCanaisReduceFinalizado;
    return listaCanaisReduceAtendimento;
  }, [valueScrollable, listaCanaisReduceGeral, listaCanaisReduceFinalizado, listaCanaisReduceAtendimento]);

  const isFiltered = tagsSelecionadas.length > 0 || funisSelecionadas.length > 0 || canaisSelecionados.length > 0 || chatsNaoLidos.length > 0 || gruposSelecionados.length > 0;

  const dataFiltered = useMemo(() => applySortFilter({
    tableData,
    tagsSelecionadas,
    funisSelecionadas,
    canaisSelecionados,
    chatsNaoLidos,
    gruposSelecionados,
    t
  }), [tableData, tagsSelecionadas, funisSelecionadas, canaisSelecionados, chatsNaoLidos, gruposSelecionados, t]);

  // Handlers with useCallback
  const handleChangeAccordion = useCallback((panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  }, []);

  // O offset é o total já carregado da lista CRUA (a lista da aba recebe
  // dataFiltered, que não serve de offset). Com o $sort global do backend
  // (lastUpdate desc + _id) isso é a continuação real da lista.
  // O ref evita repetir a mesma página enquanto a requisição está em voo ou
  // quando o usuário continua clicando com um filtro ativo.
  const ultimoOffsetPedido = useRef(-1);
  const getMoreChats = useCallback(() => {
    const offset = chatsAtendimento.length;
    if (offset === 0 || ultimoOffsetPedido.current === offset) return;
    ultimoOffsetPedido.current = offset;
    dispatch(getMaisChatsAtendimento(user.clienteId, user.id, user.setorId, offset));
  }, [dispatch, chatsAtendimento.length, user?.clienteId, user?.id, user?.setorId]);

  const handleFilterChange = useCallback((setter) => (event) => {
    const { value } = event.target;
    setter(typeof value === 'string' ? value.split(',') : value);
  }, []);

  const handleChangeTags = useCallback(handleFilterChange(setTagsSelecionadas), [handleFilterChange]);
  const handleChangeFunis = useCallback(handleFilterChange(setFunisSelecionadas), [handleFilterChange]);
  const handleChatsNaoLidos = useCallback(handleFilterChange(setChatsNaoLidos), [handleFilterChange]);
  const handleChangeCanais = useCallback(handleFilterChange(setCanaisSelecionados), [handleFilterChange]);
  const handleChangeGrupos = useCallback(handleFilterChange(setGruposSelecionados), [handleFilterChange]);

  const clearFilters = useCallback(() => {
    setTagsSelecionadas([]);
    setFunisSelecionadas([]);
    setCanaisSelecionados([]);
    setChatsNaoLidos([]);
    setGruposSelecionados([]);
  }, []);

  const limparChats = useCallback(async () => {
    try {
      setLoadingLimparChats(true);

      // O status enviado deve corresponder à aba ativa onde a finalização em massa
      // está disponível: '1' Atendendo, '3' Geral, '4' Finalizado.
      const statusPorAba = {
        '1': 'Atendendo',
        '3': 'Geral',
        '4': 'Finalizado',
      };
      const status = statusPorAba[valueScrollable] || 'Geral';

      const result = await v2_actionFinalizarMultiplosChats({
        usuarioId: user.id,
        status,
        clienteId: cliente._id,
      });
      if (!result.data.error) {
        const refreshPorAba = {
          '1': () => dispatch(getChatsAtendimento(user.clienteId, user.id, user.setorId, 30, true)),
          '3': () => dispatch(getChatsGeral(user.clienteId, user.id, user.setorId, 100, true)),
          '4': () => dispatch(getChatsFinalizados(user.clienteId, user.id, user.setorId, 50, true)),
        };
        refreshPorAba[valueScrollable]?.();
        dispatch(getCountsChats(user.clienteId, user.id, user.setorId));
      }
      enqueueSnackbar(t('sidebar.chatsFinalized'), { variant: 'success' });
    } catch (error) {
      console.error('Error clearing chats:', error);
      enqueueSnackbar(t('sidebar.clearChatsError'), { variant: 'error' });
    } finally {
      setLoadingLimparChats(false);
    }
  }, [user, cliente, dispatch, enqueueSnackbar, valueScrollable, t]);




  const SCROLLABLE_TAB = useMemo(() => [
    {
      value: '1',
      counts: countsListaChats.count_atendendo,
      label: t('sidebar.tabs.attending'),
      component: (
        <ItemAtendendo
          chats={chatsAtendimento}
          cliente={cliente}
          loading={loadingListaChatsAtendendo}
          listaFunilReduce={listaFunilReduce}
          listaTagsReduce={listaTagsReduce}
          listaCanaisReduce={listaCanaisReduce}
          dataFiltered={dataFiltered}
          expanded={expanded}
          onChangeAccordion={handleChangeAccordion}
          onMoreChats={getMoreChats}
        />
      )
    },
    {
      value: '2',
      counts: countsListaChats.count_pendente,
      label: t('sidebar.tabs.inQueue'),
      component: (
        <ItemPendente
          chats={chatsPendente}
          loading={loadingListaChatsPendente}
          listaTagsReduce={listaTagsReduce}
          listaCanaisReduce={listaCanaisReduce}
        />
      )
    },
    {
      value: '3',
      counts: countsListaChats.count_geral,
      label: t('sidebar.tabs.inBot'),
      component: (
        <ItemGeral
          chats={chatsGeral}
          chatInfo={chatInfo}
          cliente={cliente}
          loading={loadingListaChatsGeral}
          listaTagsReduce={listaTagsReduce}
          listaCanaisReduce={listaCanaisReduceGeral}
        />
      )
    },
    {
      value: '4',
      counts: countsListaChats.count_finalizado,
      label: t('sidebar.tabs.finalized'),
      component: (
        <ItemFinalizado
          chats={chatsFinalizado}
          cliente={cliente}
          loading={loadingListaChatsFinalizados}
          listaTagsReduce={listaTagsReduce}
          listaCanaisReduce={listaCanaisReduceFinalizado}
        />
      )
    }
  ], [
    t, countsListaChats, chatsAtendimento, chatsPendente, chatsGeral, chatsFinalizado,
    chatInfo, cliente, loadingListaChatsAtendendo, loadingListaChatsPendente,
    loadingListaChatsGeral, loadingListaChatsFinalizados, listaFunilReduce,
    listaTagsReduce, listaCanaisReduce, listaCanaisReduceGeral, listaCanaisReduceFinalizado,
    dataFiltered, expanded, handleChangeAccordion, getMoreChats
  ]);

  const handleChangeScrollable = useCallback((newValue) => {
    const tabActions = {
      '1': () => {
        dispatch(getChatsAtendimento(user.clienteId, user.id, user.setorId, 30, false));
        dispatch(getChatsGrupos(user.clienteId));
      },
      '2': () => dispatch(getChatsPendente(user.clienteId, user.id, user.setorId, 50, false)),
      // isStartLoading=true: sem isso startLoadingGeral nunca dispara e o
      // SkeletonListaChats do ItemGeral nunca aparece (a aba ficava em branco
      // ate a resposta). Mesmo comportamento que a aba Finalizado ja tem.
      '3': () => dispatch(getChatsGeral(cliente._id, user.id, user.setorId, 50, true)),
      '4': () => dispatch(getChatsFinalizados(cliente._id, user.id, user.setorId, 50, true))
    };

    tabActions[newValue]?.();
    setValueScrollable(newValue);
  }, [dispatch, user, cliente]);

  // Buscar grupos no mount inicial (aba Atendendo é a default)
  useEffect(() => {
    if (user?.clienteId) dispatch(getChatsGrupos(user.clienteId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.clienteId]);

  // Contador do ícone de arquivadas. Uma requisição no mount — sem isso o badge
  // só apareceria depois que o usuário abrisse a listagem, que é tarde demais.
  useEffect(() => {
    if (verifyRules(user?.rules, 'arquivo.ver')) dispatch(getDivisoes());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.clienteId]);

  // Sidebar visibility based on screen size
  useEffect(() => {
    setOpenSidebar(isDesktop);
  }, [isDesktop, pathname]);

  // Context value memoized
  const contextValue = useMemo(() => ({
    isOpen: openSidebar,
    layoutMode,
    filters: {
      tags: tagsSelecionadas,
      funis: funisSelecionadas,
      canais: canaisSelecionados,
      naoLidos: chatsNaoLidos,
      grupos: gruposSelecionados,
      isFiltered
    },
    handlers: {
      onChangeTags: handleChangeTags,
      onChangeFunis: handleChangeFunis,
      onChangeCanais: handleChangeCanais,
      onChangeNaoLidos: handleChatsNaoLidos,
      onChangeGrupos: handleChangeGrupos,
      onClearFilters: clearFilters
    }
  }), [
    openSidebar, layoutMode, tagsSelecionadas, funisSelecionadas,
    canaisSelecionados, chatsNaoLidos, gruposSelecionados, isFiltered, handleChangeTags,
    handleChangeFunis, handleChangeCanais, handleChatsNaoLidos, handleChangeGrupos, clearFilters
  ]);

  const handleCloseSearch = useCallback(() => {
    dispatch(setDrawerPesquisas(false));
  }, [dispatch]);

  const handleSearchFocus = useCallback(() => setSearchFocused(true), []);

  const handleClickAwaySearch = useCallback(() => {
    setSearchFocused(false);
    setSearchQuery('');
  }, []);

  const handleChangeSearch = useCallback(async (event) => {
    try {
      const { value } = event.target;
      setSearchQuery(value);
      dispatch(getPesquisarChats(user.clienteId, value));
      if (value === '') dispatch(setDrawerPesquisas(false));
    } catch (error) {
      console.error(error);
    }
  }, [dispatch, user?.clienteId]);

  // Só abre e atualiza o contador; a lista em si é buscada pelo
  // ChatConversationListArquivado, que já reage à divisão selecionada.
  const abrirArquivadas = useCallback(() => {
    setMostrarArquivadas(true);
    dispatch(getDivisoes());
  }, [dispatch]);

  const renderArquivadas = (
    <div className="w-full flex flex-col h-full">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-neutral-800">
        <div className="flex items-center gap-2.5">
          <Button
            onClick={() => setMostrarArquivadas(false)}
            size="icon"
            variant="ghost"
            className="w-8 h-8 rounded-lg hover:bg-gray-100 dark:hover:bg-neutral-800"
          >
            <Iconify icon="solar:arrow-left-linear" width={18} />
          </Button>
          <div className="flex flex-col">
            <span className="text-sm font-semibold text-gray-900 dark:text-white">
              {t('sidebar.archivedChats', 'Conversas arquivadas')}
            </span>
            <span className="text-[10px] text-gray-500 dark:text-neutral-400">
              {t('sidebar.archivedCount', { count: totalArquivadas, defaultValue: `${totalArquivadas} ${totalArquivadas === 1 ? 'conversa' : 'conversas'}` })}
            </span>
          </div>
        </div>
      </div>

      <ItemArquivado
        listaTagsReduce={listaTagsReduce}
        listaCanaisReduce={listaCanaisReduce}
      />
    </div>
  );

  const renderSearchResults = (
    <div className="w-full">
      {/* Search header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-neutral-800">
        <div className="flex items-center gap-2.5">
          <Button
            onClick={handleCloseSearch}
            size="icon"
            variant="ghost"
            className="w-8 h-8 rounded-lg hover:bg-gray-100 dark:hover:bg-neutral-800"
          >
            <Iconify icon="solar:arrow-left-linear" width={18} />
          </Button>
          <div className="flex flex-col">
            <span className="text-sm font-semibold text-gray-900 dark:text-white">
              {t('searchResults.title')}
            </span>
            <span className="text-[10px] text-gray-500 dark:text-neutral-400">
              {t('searchResults.foundCount', { count: pesquisasChat.length })}
            </span>
          </div>
        </div>
        {loadingPesquisarChat && (
          <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-violet-50 dark:bg-violet-500/10">
            <Iconify icon="svg-spinners:ring-resize" width={12} className="text-violet-500" />
            <span className="text-[10px] font-medium text-violet-600 dark:text-violet-400">{t('searchResults.searching')}</span>
          </div>
        )}
      </div>
      <SearchResultsScrollbar>
        <ChatListPesquisas query={searchQuery} results={pesquisasChat} callback={handleCloseSearch} />
      </SearchResultsScrollbar>
    </div>
  );

  const renderTabs = (
    <div className="flex flex-col w-full h-full">
      <Tabs forceMount onValueChange={handleChangeScrollable} value={valueScrollable} defaultValue="1" className="w-full h-full flex flex-col">
        {/* Tab navigation + filter */}
        <div className="flex items-center border-b border-gray-200 dark:border-neutral-800 mx-3">
          <TabsList className="flex flex-1 min-w-0 h-auto bg-transparent p-0 gap-0 rounded-none overflow-hidden">
            {SCROLLABLE_TAB.map((tab) => {
              const isActive = valueScrollable === tab.value;
              const colors = TAB_COLORS[tab.value];

              return (
                <TabsTrigger
                  key={tab.value}
                  value={tab.value}
                  className={`relative flex-1 flex items-center justify-center gap-1 rounded-none px-1 py-2.5 text-[12px] transition-colors border-b-2 -mb-px whitespace-nowrap
                    ${isActive
                      ? 'border-violet-500 text-gray-900 dark:text-white font-semibold'
                      : 'border-transparent text-gray-500 dark:text-neutral-400 hover:text-gray-700 dark:hover:text-neutral-300 font-medium'
                    }
                    data-[state=active]:bg-transparent data-[state=active]:shadow-none
                  `}
                >
                  {tab.label}
                  {tab.counts > 0 && (
                    <span className={`
                      min-w-[14px] h-[14px] flex items-center justify-center rounded-full px-0.5 text-[8px] font-bold leading-none
                      ${tab.value === '2'
                        ? 'bg-red-500 text-white'
                        : isActive
                          ? `${colors.bg} text-white`
                          : 'bg-gray-200 dark:bg-neutral-700 text-gray-500 dark:text-neutral-400'
                      }
                    `}>
                      {badgeFuncNumber(tab.counts)}
                    </span>
                  )}
                </TabsTrigger>
              );
            })}
          </TabsList>
        </div>

        {/* Tab content */}
        {SCROLLABLE_TAB.map((panel) => (
          <TabsContent key={panel.value} value={panel.value} className="flex-1 mt-0">
            <div className="h-full">{panel.component}</div>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );

  // Linha superior: busca + ações + criar. Ficava no Chat.js, o que empurrava os
  // botões de ação para a linha das abas e espremia os rótulos.
  const renderHeader = (
    <div className="flex items-center gap-2 py-2.5 px-3 flex-shrink-0">
      <div className="flex-1 min-w-0">
        <ChatContactSearch
          query={searchQuery}
          onFocus={handleSearchFocus}
          onChange={handleChangeSearch}
          onClickAway={handleClickAwaySearch}
          className={INPUT_AFUNDADO}
        />
      </div>

      {/* Ações agrupadas: colam entre si e se separam do "Criar" pelo divisor,
          deixando claro que são um bloco só. */}
      <div className="flex items-center gap-0.5 flex-shrink-0">
        {verifyRules(user?.rules, 'arquivo.ver') && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={abrirArquivadas}
                  className={`h-8 w-8 rounded-lg ${BOTAO_RELEVO_SECUNDARIO}`}
                >
                  <div className="relative">
                    <Iconify icon="solar:archive-bold" width={15} />
                    {totalArquivadas > 0 && (
                      <span className="absolute -top-1 -right-1 min-w-[13px] h-[13px] flex items-center justify-center rounded-full bg-violet-500 text-white text-[8px] font-bold px-0.5">
                        {badgeFuncNumber(totalArquivadas)}
                      </span>
                    )}
                  </div>
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t('sidebar.archivedChatsTooltip', 'Chats Arquivados')}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}

        <SidebarFilterButton
          filters={contextValue.filters}
          handlers={contextValue.handlers}
          listaTagsReduce={listaTagsReduce}
          listaCanaisReduce={listaCanaisReduce}
          listaFunilReduce={listaFunilReduce}
          showFunis={valueScrollable === '1'}
          showGrupos={valueScrollable === '1'}
          canMassFinalize={verifyRules(user?.rules, "finalizar_chat_massa") && ['1', '3', '4'].includes(valueScrollable)}
          onMassFinalize={limparChats}
          loadingMassFinalize={loadingLimparChats}
        />
      </div>

      <div className="w-px h-5 bg-gray-200 dark:bg-neutral-700 flex-shrink-0" />

      <MenuOptionsNovo />
    </div>
  );

  // Abas principais: Conversas | Comentarios (FB + IG mesclados). Strip proprio
  // (sem shadcn Tabs) para nao conflitar com o Tabs interno das sub-abas.
  // A aba Comentarios e gateada pela feature "socialmedia.comments" do cliente;
  // sem ela, o strip nem aparece e a sidebar fica identica ao layout original.
  const hasCommentsFeature = verifyFeatures(cliente?.features, 'socialmedia.comments');

  const MAIN_TABS = [
    { value: 'conversas', label: t('sidebar.mainTabs.conversations'), icon: null },
    { value: 'comentarios', label: t('sidebar.mainTabs.comments'), icon: 'eva:message-circle-outline', iconColor: 'text-violet-600' },
  ];

  const renderMainTabs = hasCommentsFeature && (
    <div className="flex items-center border-b border-gray-200 dark:border-neutral-800 px-3 flex-shrink-0">
      {MAIN_TABS.map((tab) => {
        const isActive = activeChatTab === tab.value;
        return (
          <button
            key={tab.value}
            onClick={() => dispatch(setActiveChatTab(tab.value))}
            className={`flex-1 flex items-center justify-center gap-1.5 px-1 py-2.5 text-[12px] transition-colors border-b-2 -mb-px whitespace-nowrap
              ${isActive
                ? 'border-violet-500 text-gray-900 dark:text-white font-semibold'
                : 'border-transparent text-gray-500 dark:text-neutral-400 hover:text-gray-700 dark:hover:text-neutral-300 font-medium'
              }`}
          >
            {tab.icon && <Iconify icon={tab.icon} width={14} className={isActive ? tab.iconColor : ''} />}
            {tab.label}
            {/* Comentarios novos chegando por socket enquanto o atendente esta em Conversas */}
            {tab.value === 'comentarios' && unreadComments > 0 && (
              <span className="min-w-[16px] h-[16px] flex items-center justify-center rounded-full bg-violet-500 text-white text-[9px] font-bold px-1 flex-shrink-0">
                {unreadComments > 99 ? '99+' : unreadComments}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );

  const isCommentsTab = hasCommentsFeature && activeChatTab === 'comentarios';

  const renderContent = (
    <div className="flex flex-col h-full w-full bg-white dark:bg-neutral-900 border-r border-gray-100 dark:border-neutral-800">
      {renderMainTabs}
      {isCommentsTab ? (
        <CommentsPostList />
      ) : (
        <>
          {renderHeader}
          <SyncWarning status={stateSyncWpp?.status} channelName={stateSyncWpp?.name_channel} />
          {drawerPesquisasChat
            ? renderSearchResults
            : mostrarArquivadas
              ? renderArquivadas
              : renderTabs}
        </>
      )}
    </div>
  );

  return (
    <ChatSidebarContext.Provider value={contextValue}>
      <div className="flex w-full h-full">
        {renderContent}
      </div>
    </ChatSidebarContext.Provider>
  );
}

function applySortFilter({ tableData, tagsSelecionadas, funisSelecionadas, canaisSelecionados, chatsNaoLidos, gruposSelecionados = [], t }) {
  let result = [...tableData];

  // Matches translated labels produced by t('sidebar.unreadChats') / t('sidebar.readChats') in any active language
  const unreadLabels = ["Chats não lidos", "Unread chats"];
  const readLabels = ["Chats lidos", "Read chats"];
  const statusMap = {};
  unreadLabels.forEach((l) => { statusMap[l] = "sim"; });
  readLabels.forEach((l) => { statusMap[l] = "nao"; });

  if (tagsSelecionadas.length > 0) {
    result = result.filter(item =>
      item.tags_ids?.some(tag => tagsSelecionadas.includes(tag.nome))
    );
  }

  if (funisSelecionadas.length > 0) {
    result = result.filter(item => funisSelecionadas.includes(item.nome_funil));
  }

  if (canaisSelecionados.length > 0) {
    result = result.filter(item => canaisSelecionados.includes(item.nome_canal));
  }

  if (chatsNaoLidos.length > 0) {
    const mappedStatuses = chatsNaoLidos.map(status => statusMap[status] || status);
    result = result.filter(item => mappedStatuses.includes(item.novaMensagem));
  }

  if (gruposSelecionados && gruposSelecionados.length > 0) {
    const groupsLabels = ["Grupos", "Groups"];
    const indivLabels = ["Individuais", "Individuals"];
    if (t) {
      groupsLabels.push(t('sidebar.groupsLabel'));
      indivLabels.push(t('sidebar.individualsLabel'));
    }
    const wantsGroups = gruposSelecionados.some(v => groupsLabels.includes(v));
    const wantsIndividuals = gruposSelecionados.some(v => indivLabels.includes(v));
    if (wantsGroups && !wantsIndividuals) {
      result = result.filter(item => item.isGroup === true);
    } else if (wantsIndividuals && !wantsGroups) {
      result = result.filter(item => !item.isGroup);
    }
  }

  return result;
}
